import type { FC, ReactNode } from 'react';
import React, { useCallback, useRef } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
// We have deprecated unstated. Please use react-sweet-state instead
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ConfluenceEdition } from '@confluence/change-edition';
import { FlagsStateContainer } from '@confluence/flags';
import type { FlagDescriptor } from '@confluence/flags';

type TrialFlagsComponentProps = {
	deactivationDateLong: string;
	edition: ConfluenceEdition | null;
	licenseConsumingUserCount: number;
	linkToAddBilling: string;
	onDismiss: () => void;
	trialDaysLeft: number;
};

type Action = {
	content: ReactNode;
	onClick?: () => void | undefined;
	href?: string | undefined;
	target?: string | undefined;
};

const i18n = defineMessages({
	titlePremium: {
		id: 'trial-components.flag.title.premium',
		defaultMessage:
			'Your Premium trial ends in {trialDaysLeft, plural, one {# day} other {# days}}',
		description: 'Title how many days are left on the trial',
	},
	titleStandard: {
		id: 'trial-components.flag.title.standard',
		defaultMessage:
			'Your Standard trial ends in {trialDaysLeft, plural, one {# day} other {# days}}',
		description: 'Title how many days are left on the trial',
	},
	removeUsersLinkText: {
		id: 'trial-components.flag.remove-users.link-text',
		defaultMessage: 'Remove users',
		description: 'Link to remove users',
	},
	infoDescription: {
		id: 'trial-components.flag.info.description',
		defaultMessage:
			'Add payment details to stay on {edition} and keep the features you love. Otherwise, your site will be downgraded to Confluence Free.',
		description:
			'Telling a user to add their payment details in order to keep the features on their current edition. {edition} will be either `Premium` or `Standard`',
	},
	warningDescription: {
		id: 'trial-components.flag.warning.description',
		defaultMessage:
			'Add payment details to stay on {edition} and keep more than 10 users, or remove users and switch to Confluence Free. <strong>If you don’t take action, your site will be deactivated on {deactivationDateLong}.</strong>',
		description:
			'Telling a user to add their payment details in order to keep the features on their current edition. {edition} will be either `Premium` or `Standard`',
	},
	addPaymentDetailsCTA: {
		id: 'trial-components.flag.add-payment-details',
		defaultMessage: 'Add payment details',
		description: 'Link to adding the payment details for Confluence',
	},
});

const LINK_TO_REMOVE_USERS =
	'https://support.atlassian.com/confluence-cloud/docs/removing-users-and-downgrading-to-confluence-free/';

type TrialFlagsComponentInnerProps = TrialFlagsComponentProps & {
	flags: FlagsStateContainer;
};

const USER_ACTION_REMOVE_USER = 'removeUser';
const USER_ACTION_ADD_BILLING = 'addBilling';

const getActionAnalyticsEvent = (
	edition: string,
	type: string,
	trialDaysLeft: number,
	userAction: string,
) => ({
	type: 'sendUIEvent',
	data: {
		action: 'clicked',
		actionSubject: 'button',
		actionSubjectId: 'trialActionComponent',
		attributes: {
			edition,
			trialDaysLeft,
			userAction,
			type,
		},
		source: 'trialFlag',
	},
});

const TrialFlagsComponentInner: FC<TrialFlagsComponentInnerProps> = ({
	deactivationDateLong,
	edition,
	flags,
	licenseConsumingUserCount,
	linkToAddBilling,
	onDismiss: onDismissStoreFlagOnboardingState,
	trialDaysLeft,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const hasShownFlagRef = useRef(false);
	const flagIdRef = useRef<string | number | null>(null);

	const onDismiss = useCallback(() => {
		flagIdRef.current && void flags.hideFlag(flagIdRef.current);
		onDismissStoreFlagOnboardingState();
	}, [flags, onDismissStoreFlagOnboardingState]);

	const handleAddBillingClick = useCallback(
		(edition: string, type: string, trialDaysLeft: number) => {
			createAnalyticsEvent(
				getActionAnalyticsEvent(edition, type, trialDaysLeft, USER_ACTION_ADD_BILLING),
			).fire();
		},
		[createAnalyticsEvent],
	);

	const handleRemoveUsersClick = useCallback(
		(edition: string, type: string, trialDaysLeft: number) => {
			createAnalyticsEvent(
				getActionAnalyticsEvent(edition, type, trialDaysLeft, USER_ACTION_REMOVE_USER),
			).fire();
		},
		[createAnalyticsEvent],
	);

	const isWarn = licenseConsumingUserCount > 10;
	const type = isWarn ? 'warning' : 'info';
	const flagId = `${edition}-trial-${trialDaysLeft}-day-${type}-flag`;

	const addPaymentDetails: Action = {
		content: <FormattedMessage {...i18n.addPaymentDetailsCTA} />,
		href: linkToAddBilling,
		target: '_blank',
		onClick: () => handleAddBillingClick(edition as string, type, trialDaysLeft),
	};
	const removeUsersAction: Action = {
		content: <FormattedMessage {...i18n.removeUsersLinkText} />,
		href: LINK_TO_REMOVE_USERS,
		target: '_blank',
		onClick: () => handleRemoveUsersClick(edition as string, type, trialDaysLeft),
	};

	const editionKey = edition === ConfluenceEdition.STANDARD ? 'Standard' : 'Premium';
	const flag: FlagDescriptor = {
		id: flagId,
		type,
		title: <FormattedMessage {...i18n[`title${editionKey}`]} values={{ trialDaysLeft }} />,
		description: (
			<FormattedMessage
				{...i18n[`${type}Description`]}
				{...{
					values: isWarn
						? {
								deactivationDateLong,
								strong: (...chunks) => <strong>{chunks}</strong>,
								edition: editionKey,
							}
						: { edition: editionKey },
				}}
			/>
		),
		actions: isWarn ? [addPaymentDetails, removeUsersAction] : [addPaymentDetails],
		onClose: onDismiss,
	};

	flagIdRef.current = flagId;

	if (hasShownFlagRef && !hasShownFlagRef.current) {
		void flags.showFlag(flag).then((newFlag) => {
			// If a new flag was created, store the new flag's ID so we can update it.  Note that
			// `showFlag()` only asynchronously returns a valid newFlag if it created a new flag;
			// if it found an existing flag with the specified ID then it asynchronously returns
			// nothing (undefined).
			if (newFlag) {
				flagIdRef.current = newFlag.id;
			}
		});
		hasShownFlagRef.current = true;
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'trialFlagScreen',
				attributes: {
					edition,
					trialDaysLeft,
					type,
				},
			},
		}).fire();
	}
	return null;
};

export const TrialFlagsComponent: FC<TrialFlagsComponentProps> = ({
	edition,
	licenseConsumingUserCount,
	linkToAddBilling,
	deactivationDateLong,
	trialDaysLeft,
	onDismiss,
}) => {
	return (
		<Subscribe to={[FlagsStateContainer]}>
			{(flags: FlagsStateContainer) => {
				return (
					<TrialFlagsComponentInner
						deactivationDateLong={deactivationDateLong}
						edition={edition}
						flags={flags}
						licenseConsumingUserCount={licenseConsumingUserCount}
						linkToAddBilling={linkToAddBilling}
						onDismiss={onDismiss}
						trialDaysLeft={trialDaysLeft}
					/>
				);
			}}
		</Subscribe>
	);
};
